import { useCallback, useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getRelatedProductsForBag } from './catalog';
import type { ProductListItemDto } from '@/services/platform/api/model';

export interface UseRelatedProductsForBagResult {
  data: ProductListItemDto[];
  isLoading: boolean;
}

/**
 * Fetches related products for the "add to bag" placement from the
 * dedicated catalog endpoint (`/catalog/products/{guid}/related-purchase`).
 * The server ranks by brand + gender, preferring products tagged "New" in
 * Shopify, falling back to most recent same-brand+gender products when
 * nothing is tagged "New". Returns an empty list while the product GUID is
 * unknown.
 */
export function useRelatedProductsForBag(productGuid?: string, limit = 6): UseRelatedProductsForBagResult {
  const locale = useLocaleStore(state => state.locale);
  const [data, setData] = useState<ProductListItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!productGuid) {
      setData([]);
      return;
    }
    setIsLoading(true);
    try {
      const items = await getRelatedProductsForBag(productGuid, limit, locale);
      setData(items ?? []);
    } finally {
      setIsLoading(false);
    }
  }, [productGuid, limit, locale]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, isLoading };
}
